import { IGlobalConfigurationModel, IEmailTemplateModel, IYouTubeVideoModel } from './../../models/global-setting/global-setting.model';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IYouTubeConfigurationsModel } from '../../models/global-setting/global-setting.model';
import { GlobalSettingAPIService } from '../api/global-setting.api.service';
import { BaseDataStore } from './base-data.store';
import { AlertService } from '../alert.service';
import { NotificationType } from 'angular2-notifications';
import { PermissionService } from '../permission.service';
import * as PermissionConstants from '../../modules/catch-core/data/constants/permission.constants';

@Injectable({ providedIn: 'root' })
export class GlobalSettingStore extends BaseDataStore {
  // Shared by loadGlobalConfig and loadPublicData - both read the same field, camelCase from a
  // JS-originated payload or PascalCase from .NET's default JSON casing (see readProperty below).
  private static readonly BLUEPRINT_RELATIONSHIP_TYPES_KEYS: [string, string] = ['blueprintRelationshipTypes', 'BlueprintRelationshipTypes'];

  private _videoSetting: BehaviorSubject<IYouTubeConfigurationsModel | null> = new BehaviorSubject<IYouTubeConfigurationsModel | null>(null);

  public $videoSetting: Observable<IYouTubeConfigurationsModel | null> = this._videoSetting.asObservable();

  private _videoList: BehaviorSubject<IYouTubeVideoModel[] | null> = new BehaviorSubject<IYouTubeVideoModel[] | null>(null);

  public $videoList: Observable<IYouTubeVideoModel[] | null> = this._videoList.asObservable();

  private _globalConfig: BehaviorSubject<IGlobalConfigurationModel | null> = new BehaviorSubject<IGlobalConfigurationModel | null>(null);

  public $globalConfig: Observable<IGlobalConfigurationModel | null> = this._globalConfig.asObservable();

  private _announcementMessage: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public $announcementMessage: Observable<string> = this._announcementMessage.asObservable();

  private _blueprintRelationshipTypes: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  public $blueprintRelationshipTypes: Observable<string[]> = this._blueprintRelationshipTypes.asObservable();

  private _userActivationEmailTemplate: BehaviorSubject<IEmailTemplateModel | null | undefined> = new BehaviorSubject<IEmailTemplateModel | null | undefined>(undefined);

  public $userActivationEmailTemplate: Observable<IEmailTemplateModel | null | undefined> = this._userActivationEmailTemplate.asObservable();

  private _organizationActivateEmailTemplate: BehaviorSubject<IEmailTemplateModel | null | undefined> = new BehaviorSubject<IEmailTemplateModel | null | undefined>(undefined);

  public $organizationActivateEmailTemplate: Observable<IEmailTemplateModel | null | undefined> = this._organizationActivateEmailTemplate.asObservable();

  //Getters:

  get videoSetting(): IYouTubeConfigurationsModel | null {
    return this._videoSetting.getValue();
  }

  get videoList(): IYouTubeVideoModel[] | null {
    return this._videoList.getValue();
  }

  get globalConfig(): IGlobalConfigurationModel | null {
    return this._globalConfig.getValue();
  }

  get announcementMessage(): string {
    return this._announcementMessage.getValue();
  }

  get blueprintRelationshipTypes(): string[] {
    return this._blueprintRelationshipTypes.getValue();
  }

  get userActivationEmailTemplate(): IEmailTemplateModel | null | undefined {
    return this._userActivationEmailTemplate.getValue();
  }

  get organizationActivateEmailTemplate(): IEmailTemplateModel | null | undefined {
    return this._organizationActivateEmailTemplate.getValue();
  }

  //Setters:

  set videoSetting(setting: IYouTubeConfigurationsModel | null) {
    this._videoSetting.next(setting);
  }

  set videoList(list: IYouTubeVideoModel[] | null) {
    this._videoList.next(list ?? []);
  }

  set globalConfig(setting: IGlobalConfigurationModel | null) {
    this._globalConfig.next(setting);
  }

  set announcementMessage(message: string) {
    this._announcementMessage.next(message);
  }

  set blueprintRelationshipTypes(types: string[] | null | undefined) {
    this._blueprintRelationshipTypes.next(types ?? []);
  }

  set userActivationEmailTemplate(template: IEmailTemplateModel | null | undefined) {
    this._userActivationEmailTemplate.next(template);
  }

  set organizationActivateEmailTemplate(template: IEmailTemplateModel | null | undefined) {
    this._organizationActivateEmailTemplate.next(template);
  }

  constructor(
    private globalSettingAPIService: GlobalSettingAPIService,
    private alertService: AlertService,
    private permissionService: PermissionService,
  ) {
    super();
    this.behaviorSubjectList.push(
      this._videoSetting,
      this._videoList,
      this._globalConfig,
      this._announcementMessage,
      this._blueprintRelationshipTypes,
      this._userActivationEmailTemplate,
      this._organizationActivateEmailTemplate,
    );
  }

  public loadInitialData(): void {
    if (!this.permissionService.hasPermission(PermissionConstants.GLOBAL_CONFIG_READ)) {
      // Non-admin roles don't hold global_config:read, so GET api/globalConfiguration would 403 -
      // see api/docs/permissions/permission-v3.md.
      return;
    }

    this.globalSettingAPIService.getGlobalConfiguration().subscribe({
      next: (rawGlobalConfig: unknown) => {
        const globalConfig = this.asRecord(rawGlobalConfig);
        if (!globalConfig) {
          return;
        }

        this.videoSetting = this.readProperty<IYouTubeConfigurationsModel[]>(globalConfig, 'youTubeConfigurations', 'YouTubeConfigurations')?.[0] ?? null;
        this.announcementMessage = this.readAnnouncementMessage(globalConfig);
        this.blueprintRelationshipTypes = this.readProperty<string[]>(globalConfig, ...GlobalSettingStore.BLUEPRINT_RELATIONSHIP_TYPES_KEYS) ?? [];

        this.videoList = this.videoSetting?.playlist ?? [];

        this.userActivationEmailTemplate = this.normalizeEmailTemplate(this.readProperty(globalConfig, 'userActivationEmailTemplate', 'UserActivationEmailTemplate'), 'User Activation');
        this.organizationActivateEmailTemplate = this.normalizeEmailTemplate(this.readProperty(globalConfig, 'organizationActivateEmailTemplate', 'OrganizationActivateEmailTemplate'), 'Organization Activation');
      },
      error: () => {
        this.alertService.alert(NotificationType.Error, 'Failed to load global settings', true);
      },
    });
  }

  public loadPublicData(): void {
    this.globalSettingAPIService.getPublicGlobalConfiguration().subscribe({
      next: (rawPublicConfig: unknown) => {
        const publicConfig = this.asRecord(rawPublicConfig);
        if (!publicConfig) {
          return;
        }

        this.videoList = this.readProperty<IYouTubeVideoModel[]>(publicConfig, 'playlist', 'Playlist') ?? [];
        this.announcementMessage = this.readProperty<string>(publicConfig, 'announcementMessage', 'AnnouncementMessage') ?? '';
        this.blueprintRelationshipTypes = this.readProperty<string[]>(publicConfig, ...GlobalSettingStore.BLUEPRINT_RELATIONSHIP_TYPES_KEYS) ?? [];
      },
      error: () => {
        this.alertService.alert(NotificationType.Error, 'Failed to load global settings', true);
      },
    });
  }

  public refreshVideos(): void {
    this.globalSettingAPIService.refreshVideos().subscribe({
      next: (x: IGlobalConfigurationModel) => {
        if (x) {
          this.alertService.alert(NotificationType.Success, 'Videos refreshed successfully', true);

          if (x.youTubeConfigurations?.length > 0 && x.youTubeConfigurations[0]) {
            this.videoList = x.youTubeConfigurations[0].playlist ?? [];
          }
        }
      },
      error: () => {
        this.alertService.alert(NotificationType.Error, 'Failed to refresh videos', true);
      },
    });
  }

  public updateGlobalConfig(newGlobalConfig: IGlobalConfigurationModel): void {
    if (!newGlobalConfig) return;
    this.globalSettingAPIService.updateGlobalConfig(newGlobalConfig).subscribe({
      next: (x) => {
        if (x) {
          this.alertService.alert(NotificationType.Success, 'Global settings saved successfully', true);
          this.videoSetting = newGlobalConfig.youTubeConfigurations?.[0] ?? this.videoSetting;
          this.blueprintRelationshipTypes = newGlobalConfig.blueprintRelationshipTypes ?? [];
          this.announcementMessage = newGlobalConfig.catchHubGlobalAnnouncement?.message ?? '';
          if (newGlobalConfig.youTubeConfigurations?.length > 0 && newGlobalConfig.youTubeConfigurations[0]) {
            this.videoList = newGlobalConfig.youTubeConfigurations[0].playlist ?? [];
          }
          if ('userActivationEmailTemplate' in newGlobalConfig) {
            this.userActivationEmailTemplate = newGlobalConfig.userActivationEmailTemplate ?? null;
          }
          if ('organizationActivateEmailTemplate' in newGlobalConfig) {
            this.organizationActivateEmailTemplate = newGlobalConfig.organizationActivateEmailTemplate ?? null;
          }
        }
      },
      error: () => {
        this.alertService.alert(NotificationType.Error, 'Failed to save global settings', true);
      },
    });
  }
  public testApiKey(apiKey: string, channelId: string, url: string): void {
    if (!apiKey || !url || !channelId) return;
    this.globalSettingAPIService.testApiKey(apiKey, channelId, url).subscribe(
      (x) => {
        if (x) {
          this.alertService.alert(NotificationType.Success, 'Googole API authentication verification is successful', true);
        }
      },
      (error) => {
        this.alertService.alert(NotificationType.Error, 'YouTube API authentication verification failed, please check your API key, channel ID and URL', true);
      },
    );
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  }

  private readProperty<T>(source: Record<string, unknown>, camelCaseKey: string, pascalCaseKey: string): T | undefined {
    if (camelCaseKey in source) {
      return source[camelCaseKey] as T;
    }
    if (pascalCaseKey in source) {
      console.warn(`Global settings API returned deprecated PascalCase property "${pascalCaseKey}".`);
      return source[pascalCaseKey] as T;
    }
    return undefined;
  }

  private readAnnouncementMessage(source: Record<string, unknown>): string {
    const announcement = this.asRecord(this.readProperty(source, 'catchHubGlobalAnnouncement', 'CatchHubGlobalAnnouncement'));
    if (!announcement) {
      return '';
    }

    return this.readProperty<string>(announcement, 'message', 'Message') ?? '';
  }

  private normalizeEmailTemplate(rawTemplate: unknown, templateName: string): IEmailTemplateModel | null {
    if (rawTemplate == null) {
      return null;
    }

    const template = this.asRecord(rawTemplate);
    if (!template) {
      console.warn(`${templateName} email template response was malformed.`, rawTemplate);
      return null;
    }

    const subject = this.readProperty<string>(template, 'subject', 'Subject');
    const body = this.readProperty<string>(template, 'body', 'Body');

    if (subject === undefined && body === undefined) {
      console.warn(`${templateName} email template response was missing subject/body fields.`, rawTemplate);
      return null;
    }

    return {
      subject: typeof subject === 'string' ? subject : '',
      body: typeof body === 'string' ? body : '',
    };
  }
}
